/* eslint-disable no-console */
import { useRef } from "react";
import { Table } from "react-bootstrap";
import { AutoSizer, List, ListRowProps, CellMeasurer, CellMeasurerCache, ListRowRenderer } from "react-virtualized";
import { UnMapAllButton } from "../Buttons/Buttons";
import { useProjects, hasMultipleTrackGroups, allowMappingFiles, hideTotalDuration } from "../Behaviors/projects";
import { transformTrackGroups, transformTracks } from "../Behaviors/trackView";
import { useStore } from "../State/zustandStore";
import { ProjectType, Resource } from "../api";
import { useValidation } from "../validation/validation";
import { TrackRow } from "./TrackRow";

interface Props {
    projectId?: string /* The '?' makes projectId optional */
}

export const TrackListAutoSizer = ({ projectId }: Props) => {
    console.log("TrackList projectId", projectId);

    const minRowHeight = 32;
    const rightMargin = 7;
    const listRef = useRef<List | null>(null);
    const workspaceState = useStore().projects;
    const projId = projectId
        ? projectId
        : workspaceState.selectedProjectId;
    const { project, getMappedResource, getTracks, getTrackGroups } = useProjects(projId!);
    const projectType = project?.projectType;

    const {
        getTrackValidationErrors,
        getTrackValidationWarnings,
        getTrackValidationInstructions,
        getTrackValidationRestrictions,
    } = useValidation(projId!);

    console.log("TrackList projectType", projectType);

    if (projectType !== ProjectType.Release && projectType !== ProjectType.Resource) {
        return null;
    }

    const multipleTrackGroups = hasMultipleTrackGroups(project!);
    const showPrompt = allowMappingFiles(project!);
    const hideDuration = hideTotalDuration(project!);
    const normalView = !projectId;
    const trackViews = multipleTrackGroups // AT-3499
        ? transformTrackGroups(getTrackGroups(project!), hideDuration)
        : transformTracks(getTracks(project!));

    console.log(`TrackList has ${trackViews.length} tracks.`);
    console.log(`normalView=${normalView} showPrompt=${showPrompt} hideDuration=${hideDuration}`);

    const cache = new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: minRowHeight * 2, // An estimate for unmeasured rows
        minHeight: minRowHeight,
    });

    const rowRenderer: ListRowRenderer = ({ key, index, style, parent }: ListRowProps) => {
        const track = trackViews[index];
        const iAudioResource = getMappedResource(track) as Resource;
        const trackValidatoinErrors = getTrackValidationErrors(project!.jobId, track);
        const trackValidationRestrictions = getTrackValidationRestrictions(project!.jobId, track);
        const trackValidationWarnings = getTrackValidationWarnings(project!.jobId, track);
        const trackValidationInstructions = getTrackValidationInstructions(project!.jobId, track, (iAudioResource !== undefined));
        const hasTrackValidationErrors = (trackValidatoinErrors?.length > 0);
        const hasTrackValidationRestrictions = (trackValidationRestrictions?.length > 0);
        const hasTrackValidationWarnings = (trackValidationWarnings?.length > 0);
        return (
            <CellMeasurer
                key={key}
                cache={cache}
                parent={parent}
                columnIndex={0} // For a List, columnIndex is usually 0
                rowIndex={index}
            >
                {({ registerChild }) => (
                    <TrackRow
                        style={style}
                        track={track}
                        projectId={projectId}
                        projectType={project?.projectType}
                        normalView={normalView}
                        multipleTrackGroups={multipleTrackGroups}
                        showPrompt={showPrompt}
                        iAudioResource={iAudioResource}
                        trackMsgs={trackValidatoinErrors.concat(trackValidationRestrictions).concat(trackValidationWarnings)}
                        hasValidationErrors={hasTrackValidationErrors}
                        hasValidationRestrictions={hasTrackValidationRestrictions}
                        hasValidationWarnings={hasTrackValidationWarnings}
                        validationInstructions={trackValidationInstructions}
                        registerChild={registerChild}
                    />
                )}
            </CellMeasurer>
        );
    };
    return (
        <AutoSizer>
            {({ height, width }) => (
                <Table role={"list"}
                    aria-labelledby="tracklist-heading"
                    className={"product-track-list mb-0  "}
                    aria-label="R2 Track List"
                    size="sm"
                    style={{ width: width + rightMargin, tableLayout: "fixed" }}>
                    <List
                        ref={listRef}
                        deferredMeasurementCache={cache}
                        width={width + rightMargin}
                        height={height - minRowHeight}
                        rowCount={trackViews.length}
                        rowHeight={cache.rowHeight}
                        rowRenderer={rowRenderer}
                        overscanRowCount={10}
                    />
                    {normalView ? /*hide if called from discrepancy report page*/
                        <>
                            <tbody className={"filler-row"}>
                                <tr className={"filler-row"} />
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td style={{ width: "100%" }} colSpan={9} />
                                    <td className={"iconic"}><UnMapAllButton /></td>
                                </tr>
                            </tfoot>
                        </>
                        : null
                    }
                </Table>
            )}
        </AutoSizer>
    );
};
